import { useState, useRef, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PdfToolbar.css';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;

// Fixed fit-to-width targets, keyed to how many of the left page-list
// sidebar / right annotate panel are open — 0, 1, or 2. Chosen by the user
// to match each layout exactly, rather than the live-measured ratio.
const FIT_WIDTH_BY_OPEN_PANELS = { 0: 2.37, 1: 2, 2: 1.64 };

// Renders each page element (the actual on-screen .q1-sheet etc. nodes)
// to a canvas at a fixed high resolution and assembles a real multi-page
// PDF file, then triggers a browser download — no server round-trip
// needed since every *PrintPage.jsx already renders the full page
// content client-side.
//
// The PDF page size is derived directly from each captured canvas's own
// pixel dimensions (at 96 CSS px/inch, matching the .q1-sheet etc. 794x1123
// boxes) instead of a generic 'a4' preset — jsPDF's addImage stretches the
// image to whatever page size you give it, and a mismatched aspect ratio
// or size was what produced the blurry/stretched output.
const CSS_PX_PER_PT = 96 / 72;

// html2canvas doesn't always honor `border-collapse: collapse` the way a
// browser's native table layout does — adjacent cells' border-top and the
// previous row's border-bottom can both get rasterized instead of merging
// into one shared line, producing a doubled hairline at every row boundary
// (only visible in the rasterized download, never in the live on-screen
// preview, which a real browser renders natively). Worked around here by
// rewriting every table's borders in the html2canvas clone only: switch to
// border-collapse: separate with zero spacing, and drop border-top on every
// row except the first, so each inner row boundary is drawn exactly once
// (by the row above's border-bottom) while the table's own top edge —
// which has no row above it to supply that line — is left intact.
function fixTableBordersForCapture(clonedDoc) {
    clonedDoc.querySelectorAll('table').forEach((table) => {
        table.style.borderCollapse = 'separate';
        table.style.borderSpacing = '0';
        const rows = table.querySelectorAll('tr');
        rows.forEach((row, rowIndex) => {
            if (rowIndex === 0) return;
            row.querySelectorAll('th, td').forEach((cell) => {
                if (getComputedStyle(cell).borderTopWidth !== '0px') {
                    cell.style.borderTop = 'none';
                }
            });
        });
    });
}

async function generatePdfBlob(pageElements, captureScale = 4) {
    let doc = null;

    for (let i = 0; i < pageElements.length; i++) {
        const canvas = await html2canvas(pageElements[i], {
            scale: captureScale,
            useCORS: true,
            backgroundColor: '#ffffff',
            onclone: fixTableBordersForCapture,
        });
        // JPEG at high quality encodes dramatically faster than PNG for a
        // canvas this large (scale 4 on a 794x1123 page is ~3176x4492px)
        // with no visible loss for this content (text/lines on a white
        // background) — PNG's lossless compression was the main cost of
        // generating the PDF, not the html2canvas rasterization itself.
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const pageWidthPt = canvas.width / captureScale / CSS_PX_PER_PT;
        const pageHeightPt = canvas.height / captureScale / CSS_PX_PER_PT;

        if (!doc) {
            doc = new jsPDF({
                unit: 'pt',
                format: [pageWidthPt, pageHeightPt],
                orientation: pageWidthPt > pageHeightPt ? 'landscape' : 'portrait',
                compress: true,
            });
        } else {
            doc.addPage([pageWidthPt, pageHeightPt]);
        }

        doc.addImage(imgData, 'JPEG', 0, 0, pageWidthPt, pageHeightPt);
    }

    return doc.output('blob');
}

function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// Shared zoom/sidebar state for every *PrintPage.jsx. Wrap the page's
// existing print-canvas content with <PdfCanvas> and render <PdfToolbar>
// + <PdfSidebar> above/alongside it — no pagination/data logic here,
// purely view controls.
export function usePdfView(pageCount = 1) {
    const [zoom, setZoom] = useState(1);
    // 'width' | 'height' — which axis the last "fit" button press matched
    // to the viewport. Drives both the computed zoom level and which icon
    // the fit button shows next (it always offers to switch to the OTHER
    // mode, so the icon shown is the opposite of the current mode).
    const [fitMode, setFitMode] = useState('height');
    const canvasRef = useRef(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [downloading, setDownloading] = useState(false);
    const [annotateOpen, setAnnotateOpen] = useState(false);
    const [penSize, setPenSize] = useState(2);
    const [penColor, setPenColor] = useState('#000000');
    const [highlightColor, setHighlightColor] = useState('#f28b82');
    // 'pen' | 'highlighter' | 'eraser'
    const [tool, setTool] = useState('pen');
    const pageRefs = useRef([]);
    // One <canvas> per page, registered by PdfAnnotationLayer, plus each
    // page's redraw() function so undo/redo and clear can repaint strokes
    // without reaching into canvas internals from here.
    const annotationRefs = useRef([]);
    const annotationApis = useRef([]);
    // `strokes` is the live, flat list of every stroke across all pages
    // (in draw order) — the single source of truth for what's drawn.
    // `undoStack`/`redoStack` hold past snapshots of that array, so one
    // undo/redo step is "swap `strokes` for the array on top of a stack,
    // pushing the current one onto the other stack." Treating "clear" as
    // just another snapshot (an empty array) means it undoes/redoes the
    // same way a single stroke would, with no special-case logic.
    const [strokes, setStrokes] = useState([]);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const registerPageRef = useCallback((index, el) => {
        pageRefs.current[index] = el;
    }, []);

    const goToPage = useCallback((index) => {
        const el = pageRefs.current[index];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setCurrentPage(index + 1);
    }, []);

    // Track which page is currently in view so the toolbar's page
    // indicator stays accurate while the user scrolls manually.
    useEffect(() => {
        const elements = pageRefs.current.filter(Boolean);
        if (elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                if (visible) {
                    const idx = pageRefs.current.indexOf(visible.target);
                    if (idx !== -1) setCurrentPage(idx + 1);
                }
            },
            { threshold: [0.5] }
        );
        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [pageCount]);

    const downloadPdf = useCallback(async (filename = 'document.pdf') => {
        const elements = pageRefs.current.filter(Boolean);
        if (elements.length === 0 || downloading) return;
        setDownloading(true);
        // Capture at 1x zoom regardless of the current on-screen zoom level —
        // html2canvas renders the element's live CSS transform, so a zoomed
        // view (e.g. 50%) would otherwise shrink the captured canvas and
        // produce a blurry/undersized PDF once upscaled to page size.
        const zoomBeforeCapture = zoom;
        if (zoomBeforeCapture !== 1) setZoom(1);
        try {
            if (zoomBeforeCapture !== 1) await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
            const blob = await generatePdfBlob(elements);
            triggerBlobDownload(blob, filename);
        } finally {
            if (zoomBeforeCapture !== 1) setZoom(zoomBeforeCapture);
            setDownloading(false);
        }
    }, [downloading, zoom]);

    const setPage = useCallback((pageNumber) => {
        const clamped = Math.min(Math.max(1, Math.round(pageNumber) || 1), pageCount);
        goToPage(clamped - 1);
    }, [goToPage, pageCount]);

    const setZoomPercent = useCallback((percent) => {
        const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (Number(percent) || 100) / 100));
        setZoom(+clamped.toFixed(2));
    }, []);

    const registerCanvasRef = useCallback((el) => {
        canvasRef.current = el;
    }, []);

    // Computes zoom from the CURRENT page's real, unscaled layout size
    // (pageRefs' offsetWidth/offsetHeight — unaffected by the CSS
    // transform: scale() the viewer applies, since offsetWidth reflects
    // the element's own box before any transform) against the actual
    // available viewport (canvasRef's clientWidth/clientHeight, which
    // already excludes the page-list sidebar and annotate panel via their
    // margin-left/margin-right on that same element). Using live
    // measurements instead of hardcoded 794/1123 or panel-width constants
    // keeps this correct for any format's sheet size and any sidebar/panel
    // combination, without duplicating those numbers here.
    const fitToMode = useCallback((mode) => {
        const container = canvasRef.current;
        const page = pageRefs.current[currentPage - 1] || pageRefs.current.find(Boolean);
        if (!container || !page) return;

        let next;
        if (mode === 'width') {
            // Fixed targets rather than a live-measured ratio: how many of
            // the left page-list sidebar / right annotate panel are open
            // (0, 1, or 2) selects the exact zoom percentage requested,
            // instead of whatever the viewport happens to measure out to.
            const openPanels = (sidebarOpen ? 1 : 0) + (annotateOpen ? 1 : 0);
            next = FIT_WIDTH_BY_OPEN_PANELS[openPanels];
        } else {
            // Fixed fit-to-page/height target, same reasoning as fit-width
            // above — 79% is what fits the whole A4 page vertically in the
            // viewport.
            next = 0.79;
        }

        // There is no JS API to read or set the BROWSER's own zoom level
        // (Ctrl +/-, pinch-zoom) — browsers block that for security. What
        // CAN be read is window.devicePixelRatio, which scales up with
        // browser zoom (e.g. ~1.25 at 125% browser zoom on a standard
        // 1x display). Dividing our target by that ratio compensates for
        // it: if the user is at 125% browser zoom, our 79%/fixed-width
        // value is scaled down so the PAGE still renders at the same
        // physical/visual size as it would at 100% browser zoom — the
        // browser's own zoom indicator itself can't be changed from here.
        const browserZoomCompensation = window.devicePixelRatio || 1;
        next = next / browserZoomCompensation;

        setFitMode(mode);
        setZoom(+Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)).toFixed(2));
    }, [currentPage, sidebarOpen, annotateOpen]);

    // Always switches to the OTHER axis from whichever one is active, so
    // one button press toggles fit-width <-> fit-height/page — matching
    // the icon, which always shows the mode this press will switch TO.
    const toggleFitMode = useCallback(() => {
        fitToMode(fitMode === 'width' ? 'height' : 'width');
    }, [fitMode, fitToMode]);

    // Keeps fit-width's zoom on its target percentage live as the page-list
    // sidebar / annotate panel open or close, rather than only updating at
    // the moment the fit button was pressed. fitToMode itself is stable
    // across sidebarOpen/annotateOpen changes only in identity (it's
    // recreated), not in the effect looping — it only ever calls setZoom,
    // never touches sidebarOpen/annotateOpen, so this can't re-trigger itself.
    useEffect(() => {
        if (fitMode === 'width') fitToMode('width');
    }, [sidebarOpen, annotateOpen, fitMode, fitToMode]);

    // `api` is { redraw(strokes) } supplied by each PdfAnnotationLayer —
    // redrawing from the stroke list (rather than manipulating pixels
    // directly) is what makes undo/redo and clear simple: `strokes` is the
    // single source of truth, and every history operation just swaps it
    // for a different array and asks each page to repaint from scratch.
    const registerAnnotationRef = useCallback((index, el, api) => {
        annotationRefs.current[index] = el;
        annotationApis.current[index] = api || null;
    }, []);

    const redrawAll = useCallback((strokeList) => {
        annotationApis.current.forEach((api, index) => {
            if (api) api.redraw(strokeList.filter((s) => s.page === index));
        });
    }, []);

    // A finished stroke pushes the pre-stroke array onto undo and clears
    // redo — standard undo/redo semantics (drawing something new after an
    // undo discards the undone redo branch instead of keeping it around).
    const commitStroke = useCallback((stroke) => {
        setStrokes((prev) => {
            const next = [...prev, stroke];
            setUndoStack((u) => [...u, prev]);
            setRedoStack([]);
            redrawAll(next);
            return next;
        });
    }, [redrawAll]);

    const undo = useCallback(() => {
        setUndoStack((uPrev) => {
            if (uPrev.length === 0) return uPrev;
            const restored = uPrev[uPrev.length - 1];
            setStrokes((current) => {
                setRedoStack((r) => [...r, current]);
                redrawAll(restored);
                return restored;
            });
            return uPrev.slice(0, -1);
        });
    }, [redrawAll]);

    const redo = useCallback(() => {
        setRedoStack((rPrev) => {
            if (rPrev.length === 0) return rPrev;
            const restored = rPrev[rPrev.length - 1];
            setStrokes((current) => {
                setUndoStack((u) => [...u, current]);
                redrawAll(restored);
                return restored;
            });
            return rPrev.slice(0, -1);
        });
    }, [redrawAll]);

    const clearAnnotations = useCallback(() => {
        setStrokes((current) => {
            if (current.length === 0) return current;
            setUndoStack((u) => [...u, current]);
            setRedoStack([]);
            redrawAll([]);
            return [];
        });
    }, [redrawAll]);

    return {
        zoom,
        fitMode,
        registerCanvasRef,
        toggleFitMode,
        sidebarOpen,
        currentPage,
        pageCount,
        downloading,
        pageRefs,
        registerPageRef,
        goToPage,
        setPage,
        setZoomPercent,
        downloadPdf,
        annotateOpen,
        penSize,
        penColor,
        highlightColor,
        tool,
        setPenSize,
        setPenColor,
        setHighlightColor,
        setTool,
        registerAnnotationRef,
        commitStroke,
        clearAnnotations,
        undo,
        redo,
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,
        toggleAnnotate: () => setAnnotateOpen((a) => !a),
        closeAnnotate: () => setAnnotateOpen(false),
        zoomIn: () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2))),
        zoomOut: () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2))),
        zoomReset: () => setZoom(1),
        toggleSidebar: () => setSidebarOpen((s) => !s),
        closeSidebar: () => setSidebarOpen(false),
    };
}

// Inline SVG icons. These replace the Unicode glyphs the toolbar used to
// render (⬇ ☰ ⤢ 🖶): those fall back to whatever font on the system happens
// to carry them, so they came out inconsistently sized and styled — 🖶 in
// particular renders as a colour emoji in some fonts. SVG draws identically
// everywhere and inherits the button's colour via currentColor.
const Icon = ({ path, filled = false }) => (
    <svg className="pdf-icon" viewBox="0 0 24 24" aria-hidden="true"
        fill={filled ? 'currentColor' : 'none'}
        stroke={filled ? 'none' : 'currentColor'}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={path} />
    </svg>
);

const ICONS = {
    menu: 'M3 6h18M3 12h18M3 18h18',
    minus: 'M5 12h14',
    plus: 'M12 5v14M5 12h14',
    download: 'M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2',
    print: 'M6 9V3h12v6M6 18H4a1 1 0 01-1-1v-5a2 2 0 012-2h14a2 2 0 012 2v5a1 1 0 01-1 1h-2M6 14h12v7H6z',
    pen: 'M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z',
    eraser: 'M20 20H7L3 16a2 2 0 010-3L13 3a2 2 0 013 0l5 5a2 2 0 010 3l-9 9M6 13l6 6',
    highlighter: 'M9 11l6-6 4 4-6 6-4-4zM7 13l4 4-2 2H5v-4l2-2zM13 7l4 4',
    undo: 'M3 10h10a5 5 0 010 10H9M3 10l5-5M3 10l5 5',
    redo: 'M21 10H11a5 5 0 000 10h4M21 10l-5-5M21 10l-5 5',
    // Fit-to-width/height toggle. The icon shows a page outline in the
    // orientation the button will switch TO: a tall rectangle (fit the
    // whole page's height to the screen — i.e. "page" view) when currently
    // fit-to-width, and a wide rectangle (fit the page's width edge-to-edge
    // — i.e. landscape-filled view) when currently fit-to-height.
    pageTall: 'M8 3h8a1 1 0 011 1v16a1 1 0 01-1 1H8a1 1 0 01-1-1V4a1 1 0 011-1z',
    pageWide: 'M3 8v8a1 1 0 001 1h16a1 1 0 001-1V8a1 1 0 00-1-1H4a1 1 0 00-1 1z',
};

// Editable numeric field that shows `displayValue` when not focused and
// lets the user type a new number, committing it (via Enter or blur) with
// `onCommit`. Kept generic so both the page-number and zoom-percent
// fields below share the same edit/commit behavior.
function PdfEditableField({ className, displayValue, value, onCommit, title }) {
    const [editValue, setEditValue] = useState(null);
    const isEditing = editValue !== null;

    const commit = () => {
        if (editValue !== null && editValue !== '') onCommit(Number(editValue));
        setEditValue(null);
    };

    return (
        <input
            className={className}
            type="text"
            inputMode="numeric"
            title={title}
            value={isEditing ? editValue : displayValue}
            onFocus={(e) => { setEditValue(String(value)); e.target.select(); }}
            onChange={(e) => setEditValue(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
                if (e.key === 'Escape') setEditValue(null);
            }}
        />
    );
}

export function PdfToolbar({ pageCount = 1, view, onPrint, downloadFilename = 'document.pdf' }) {
    const handlePrint = onPrint || (() => window.print());
    const handleDownload = () => view.downloadPdf(downloadFilename);

    return (
        <div className="pdf-toolbar">
            <div className="pdf-toolbar-group">
                <button className="pdf-icon-btn" onClick={view.toggleSidebar} aria-label="Toggle page list" title="Pages">
                    <Icon path={ICONS.menu} />
                </button>
                <PdfEditableField
                    className="pdf-page-indicator"
                    title="Go to page"
                    value={view.currentPage}
                    displayValue={view.currentPage}
                    onCommit={view.setPage}
                />
                <span className="pdf-page-indicator-sep">/ {pageCount}</span>
            </div>
            <div className="pdf-toolbar-group">
                <button className="pdf-icon-btn" onClick={view.zoomOut} aria-label="Zoom out" title="Zoom out">
                    <Icon path={ICONS.minus} />
                </button>
                <PdfEditableField
                    className="pdf-zoom-indicator"
                    title="Set zoom"
                    value={Math.round(view.zoom * 100)}
                    displayValue={`${Math.round(view.zoom * 100)}%`}
                    onCommit={view.setZoomPercent}
                />
                <button className="pdf-icon-btn" onClick={view.zoomIn} aria-label="Zoom in" title="Zoom in">
                    <Icon path={ICONS.plus} />
                </button>
                <button
                    className="pdf-icon-btn"
                    onClick={view.toggleFitMode}
                    aria-label={view.fitMode === 'width' ? 'Fit to page' : 'Fit to width'}
                    title={view.fitMode === 'width' ? 'Fit to page' : 'Fit to width'}
                >
                    <Icon path={view.fitMode === 'width' ? ICONS.pageTall : ICONS.pageWide} filled />
                </button>
                <button
                    className={`pdf-icon-btn${view.annotateOpen ? ' pdf-icon-btn-active' : ''}`}
                    onClick={view.toggleAnnotate}
                    aria-label="Annotate" aria-pressed={view.annotateOpen} title="Draw"
                >
                    <Icon path={ICONS.pen} />
                </button>
                {view.annotateOpen && (
                    <>
                        <button className="pdf-icon-btn" onClick={view.undo} disabled={!view.canUndo} aria-label="Undo" title="Undo">
                            <Icon path={ICONS.undo} />
                        </button>
                        <button className="pdf-icon-btn" onClick={view.redo} disabled={!view.canRedo} aria-label="Redo" title="Redo">
                            <Icon path={ICONS.redo} />
                        </button>
                    </>
                )}
            </div>
            <div className="pdf-toolbar-group">
                <button className="pdf-icon-btn" onClick={handleDownload} disabled={view.downloading} aria-label="Download" title="Download">
                    {view.downloading ? <span className="pdf-spinner" /> : <Icon path={ICONS.download} />}
                </button>
                <button className="pdf-icon-btn" onClick={handlePrint} aria-label="Print" title="Print">
                    <Icon path={ICONS.print} />
                </button>
            </div>
        </div>
    );
}

// Live, non-interactive scaled-down copy of a page element's current DOM
// (same content the user sees on screen, shrunk via CSS transform) —
// gives each sidebar thumbnail a real preview instead of a blank card.
function PdfSidebarThumbPreview({ view, index }) {
    const hostRef = useRef(null);

    useEffect(() => {
        const host = hostRef.current;
        const source = view.pageRefs.current[index];
        if (!host || !source) return;

        const clone = source.cloneNode(true);
        clone.removeAttribute('id');
        host.innerHTML = '';
        host.appendChild(clone);

        const scale = host.clientWidth / source.offsetWidth;
        clone.style.width = `${source.offsetWidth}px`;
        clone.style.height = `${source.offsetHeight}px`;
        clone.style.transform = `scale(${scale})`;
        clone.style.transformOrigin = 'top left';
        clone.style.pointerEvents = 'none';

        return () => { host.innerHTML = ''; };
    }, [view, index]);

    return <span ref={hostRef} className="pdf-sidebar-thumb-page" />;
}

export function PdfSidebar({ view }) {
    if (!view.sidebarOpen) return null;

    const pages = Array.from({ length: view.pageCount }, (_, i) => i);

    return (
        <div className="pdf-sidebar">
            {pages.map((idx) => (
                <button
                    key={idx}
                    className={`pdf-sidebar-thumb${view.currentPage === idx + 1 ? ' pdf-sidebar-thumb-active' : ''}`}
                    onClick={() => view.goToPage(idx)}
                >
                    <PdfSidebarThumbPreview view={view} index={idx} />
                    <span className="pdf-sidebar-thumb-label">{idx + 1}</span>
                </button>
            ))}
        </div>
    );
}

const PEN_SIZES = [1, 2, 4, 8, 12];
// Pen: opaque ink, drawn with normal source-over.
const PEN_COLORS = [
    '#000000', '#5f6368', '#9aa0a6', '#dadce0', '#ffffff',
    '#f28b82', '#fdd663', '#81c995', '#8ab4f8', '#fcc9a8',
    '#ea4335', '#fbbc04', '#34a853', '#4285f4', '#e8956d',
];
// Highlighter: translucent pastel palette, drawn with a 'multiply' blend
// (like a real highlighter — overlaps darken instead of just averaging)
// and a flat cap so strokes look like broad highlighter marks, not ink.
const HIGHLIGHT_COLORS = ['#f28b82', '#fdd663', '#81c995', '#8ab4f8', '#fcc9a8', '#d7aefb', '#a7ffeb', '#f6aea9'];

export function PdfAnnotatePanel({ view }) {
    if (!view.annotateOpen) return null;

    const isHighlighter = view.tool === 'highlighter';
    const colors = isHighlighter ? HIGHLIGHT_COLORS : PEN_COLORS;
    const activeColor = isHighlighter ? view.highlightColor : view.penColor;
    const setColor = isHighlighter ? view.setHighlightColor : view.setPenColor;

    return (
        <div className="pdf-annotate-panel">
            <div className="pdf-annotate-tools">
                <button
                    className={`pdf-icon-btn pdf-annotate-tool${view.tool === 'pen' ? ' pdf-icon-btn-active' : ''}`}
                    onClick={() => view.setTool('pen')}
                    aria-pressed={view.tool === 'pen'} title="Pen"
                >
                    <Icon path={ICONS.pen} />
                </button>
                <button
                    className={`pdf-icon-btn pdf-annotate-tool${view.tool === 'highlighter' ? ' pdf-icon-btn-active' : ''}`}
                    onClick={() => view.setTool('highlighter')}
                    aria-pressed={view.tool === 'highlighter'} title="Highlighter"
                >
                    <Icon path={ICONS.highlighter} />
                </button>
                <button
                    className={`pdf-icon-btn pdf-annotate-tool${view.tool === 'eraser' ? ' pdf-icon-btn-active' : ''}`}
                    onClick={() => view.setTool('eraser')}
                    aria-pressed={view.tool === 'eraser'} title="Eraser"
                >
                    <Icon path={ICONS.eraser} />
                </button>
            </div>

            <div className="pdf-annotate-section-title">Size</div>
            <div className="pdf-annotate-sizes">
                {PEN_SIZES.map((size) => (
                    <button
                        key={size}
                        className={`pdf-annotate-size${view.penSize === size ? ' pdf-annotate-size-active' : ''}`}
                        onClick={() => view.setPenSize(size)}
                        aria-label={`Size ${size}`} aria-pressed={view.penSize === size}
                    >
                        <span style={{ width: `${size}px`, height: `${size}px` }} />
                    </button>
                ))}
            </div>

            {view.tool !== 'eraser' && (
                <>
                    <div className="pdf-annotate-section-title">Color</div>
                    <div className="pdf-annotate-colors">
                        {colors.map((color) => (
                            <button
                                key={color}
                                className={`pdf-annotate-color${activeColor === color ? ' pdf-annotate-color-active' : ''}${isHighlighter ? ' pdf-annotate-color-translucent' : ''}`}
                                style={{ background: color }}
                                onClick={() => setColor(color)}
                                aria-label={color} aria-pressed={activeColor === color}
                            />
                        ))}
                    </div>
                </>
            )}

            <button className="pdf-annotate-clear" onClick={view.clearAnnotations}>
                Clear all
            </button>
        </div>
    );
}

// Transparent <canvas> sized to its page's exact pixel box, laid over the
// page content. It only takes pointer events while annotate mode is on, so
// text stays selectable otherwise. Because downloads rasterise the live DOM
// via html2canvas, strokes drawn here appear in the downloaded PDF too.
//
// Strokes are accumulated as point arrays and committed to `view`'s shared
// history on pointer-up, rather than drawn directly and left as pixels —
// that's what lets undo/redo and clear work: `redraw()` below just clears
// the canvas and replays whichever strokes `view` currently says belong to
// this page, so every history operation is "decide which strokes exist,"
// never "figure out how to subtract pixels that were already drawn."
function paintStroke(ctx, stroke) {
    if (stroke.points.length === 0) return;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = stroke.size;
    if (stroke.tool === 'eraser') {
        // Painted as destination-out in the same draw-order replay as every
        // other stroke, so an eraser stroke only erases what was already
        // painted before it — exactly like a real eraser, and (because it's
        // just another entry in the shared stroke array) undo/redo and
        // "Clear all" cover it the same as pen/highlighter strokes.
        ctx.globalCompositeOperation = 'destination-out';
    } else if (stroke.tool === 'highlighter') {
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = 0.4;
        ctx.lineWidth = stroke.size * 6;
    }
    ctx.strokeStyle = stroke.color;
    ctx.beginPath();
    const [first, ...rest] = stroke.points;
    ctx.moveTo(first.x, first.y);
    if (rest.length === 0) ctx.lineTo(first.x + 0.01, first.y + 0.01);
    rest.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.stroke();
    ctx.restore();
}

export function PdfAnnotationLayer({ view, index, width, height }) {
    const canvasRef = useRef(null);
    const currentStroke = useRef(null);
    const strokesRef = useRef([]);

    const redraw = useCallback((pageStrokes) => {
        strokesRef.current = pageStrokes;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        pageStrokes.forEach((s) => paintStroke(ctx, s));
    }, []);

    useEffect(() => {
        view.registerAnnotationRef(index, canvasRef.current, { redraw });
        return () => view.registerAnnotationRef(index, null, null);
    }, [view, index, redraw]);

    // The canvas backing store is fixed at the page's CSS pixel size, so it
    // is unaffected by the viewer's zoom — pointer coords are converted from
    // the on-screen rect below, which keeps strokes aligned at any zoom.
    const toCanvasPoint = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (width / rect.width),
            y: (e.clientY - rect.top) * (height / rect.height),
        };
    };

    const start = (e) => {
        if (!view.annotateOpen) return;
        e.preventDefault();
        const isEraser = view.tool === 'eraser';
        currentStroke.current = {
            page: index,
            tool: isEraser ? 'eraser' : view.tool,
            color: isEraser ? '#000000' : (view.tool === 'highlighter' ? view.highlightColor : view.penColor),
            size: isEraser ? Math.max(view.penSize * 4, 12) : view.penSize,
            points: [toCanvasPoint(e)],
        };
        canvasRef.current.setPointerCapture(e.pointerId);
    };

    const move = (e) => {
        if (!currentStroke.current) return;
        currentStroke.current.points.push(toCanvasPoint(e));

        // Redraw every already-committed stroke plus this in-progress one
        // on each move, rather than incrementally stroking segment-by-
        // segment, so a highlighter's 'multiply' blend/alpha (and an
        // eraser's destination-out) read as one continuous mark instead of
        // visibly double-applying at every segment overlap.
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        strokesRef.current.forEach((s) => paintStroke(ctx, s));
        paintStroke(ctx, currentStroke.current);
    };

    const end = (e) => {
        if (!currentStroke.current) return;
        const stroke = currentStroke.current;
        currentStroke.current = null;
        try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* pointer already released */ }
        if (stroke.points.length < 1) return;
        view.commitStroke(stroke);
    };

    return (
        <canvas
            ref={canvasRef}
            className={`pdf-annotation-layer${view.annotateOpen ? ' pdf-annotation-layer-active' : ''}`}
            width={width}
            height={height}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
        />
    );
}

export function PdfCanvas({ view, className, children }) {
    const elRef = useRef(null);

    // Ctrl/Cmd + scroll-wheel is the browser's native zoom gesture on
    // desktop — it resizes the real browser viewport, which our app's
    // `view.zoom` state has no way to find out about (there's no reliable
    // JS event for it; visualViewport.scale, which DOES fire for pinch-
    // zoom, stays at 1 for this gesture in most desktop browsers). So
    // instead of trying to detect it after the fact, this intercepts the
    // wheel event here and preventDefault()s it before the browser acts on
    // it, driving view.zoomIn/zoomOut instead — only ONE zoom ever changes
    // (ours), so there's nothing left to go out of sync.
    //
    // React's onWheel is a passive listener by default in some setups
    // (for scroll performance), and preventDefault() on a passive listener
    // is silently ignored — so this is wired as a real addEventListener
    // with { passive: false } instead of JSX's onWheel prop, which is the
    // only way to reliably block the browser's own zoom here.
    useEffect(() => {
        const el = elRef.current;
        if (!el) return;

        const handleWheel = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            if (e.deltaY < 0) view.zoomIn();
            else if (e.deltaY > 0) view.zoomOut();
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [view]);

    const setRefs = (el) => {
        elRef.current = el;
        view.registerCanvasRef(el);
    };

    const style = {
        transform: `scale(${view.zoom})`,
        transformOrigin: 'top center',
    };
    const extra = `${view.sidebarOpen ? ' pdf-canvas-with-sidebar' : ''}${view.annotateOpen ? ' pdf-canvas-with-annotate' : ''}`;
    return (
        <div ref={setRefs} className={`${className}${extra}`}>
            <div className="pdf-zoom-wrapper" style={style}>
                {children}
            </div>
        </div>
    );
}

export function PdfPage({ view, index, className, style, children }) {
    const [size, setSize] = useState(null);

    // Measured from the element rather than hardcoded to 794x1123 so the
    // annotation canvas still matches if a format uses a different sheet.
    const ref = useCallback((el) => {
        view.registerPageRef(index, el);
        if (el) setSize({ width: el.offsetWidth, height: el.offsetHeight });
    }, [view, index]);

    return (
        <div ref={ref} className={className} style={style}>
            {children}
            {size && <PdfAnnotationLayer view={view} index={index} width={size.width} height={size.height} />}
        </div>
    );
}
